import { motion } from "framer-motion";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { headingFade } from "@/lib/motion";

// Button-styled affordance shown inside each leader card linking to the
// full founder profile (/joe, /andy).
const VIEW_PROFILE_CLASS =
  "mt-5 inline-flex items-center gap-2 self-start rounded-md border [border-color:var(--button-outline)] bg-secondary/40 px-4 py-2 text-sm font-medium text-foreground transition-colors group-hover:border-primary/50 group-hover:text-primary";

const leaderEntrance = (fromX: number) => ({
  hidden: { opacity: 0, x: fromX, scale: 0.94 },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: { type: "spring" as const, stiffness: 130, damping: 15, mass: 0.9 },
  },
});

export function Leadership() {
  return (
    <section id="leadership" className="py-24 md:py-32 relative">
      <div className="container mx-auto px-6 md:px-12">
        <motion.div
          className="max-w-3xl mb-16"
          variants={headingFade}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <h2 className="text-primary font-mono text-sm tracking-widest uppercase mb-3">Leadership</h2>
          <h3 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-6">
            Navigating the frontier.
          </h3>
          <p className="text-lg text-muted-foreground">
            Decades of combined experience in enterprise technology, process architecture, and financial management, now focused entirely on the SMB sector.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={leaderEntrance(40)}
            transition={{ delay: 0.15 }}
          >
            <Link href="/andy" className="block h-full" aria-label="View Andy Dai's profile">
              <Card className="bg-card overflow-hidden h-full border-border/50 group cursor-pointer transition-colors hover:border-primary/40">
                <CardContent className="p-0 flex flex-col sm:flex-row h-full">
                  <div className="w-full sm:w-2/5 md:w-1/3 aspect-square sm:aspect-auto relative bg-secondary">
                    <img
                      src={`${import.meta.env.BASE_URL}assets/andy-1200.webp`}
                      srcSet={`${import.meta.env.BASE_URL}assets/andy-600.webp 600w, ${import.meta.env.BASE_URL}assets/andy-1200.webp 1200w`}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 40vw, 33vw"
                      alt="Andy Dai"
                      width={1200}
                      height={1609}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover grayscale opacity-80 mix-blend-luminosity group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground" style="background-color: %231a1f2e;"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>';
                      }}
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-black/10 dark:ring-white/10" />
                  </div>
                  <div className="p-6 sm:p-8 w-full sm:w-3/5 md:w-2/3 flex flex-col justify-center">
                    <h4 className="text-2xl font-display font-bold text-foreground mb-1">Andy Dai</h4>
                    <p className="text-primary font-mono text-sm tracking-wider mb-4">Chief AI Officer</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Andy leverages his deep understanding of data engineering and finance to optimize customer functions and processes through the use of AI. His expertise in AI automations, image and video generation, editing and animation, logistics, and financial management provides a competitive advantage in our customers' operations.
                    </p>
                    <span className={VIEW_PROFILE_CLASS}>
                      View Profile
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={leaderEntrance(-40)}
          >
            <Link href="/joe" className="block h-full" aria-label="View Joe Johnson's profile">
              <Card className="bg-card overflow-hidden h-full border-border/50 group cursor-pointer transition-colors hover:border-primary/40">
                <CardContent className="p-0 flex flex-col sm:flex-row h-full">
                  <div className="w-full sm:w-2/5 md:w-1/3 aspect-square sm:aspect-auto relative bg-secondary">
                    <img
                      src={`${import.meta.env.BASE_URL}assets/joe-1200.webp`}
                      srcSet={`${import.meta.env.BASE_URL}assets/joe-600.webp 600w, ${import.meta.env.BASE_URL}assets/joe-1200.webp 1200w`}
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 40vw, 33vw"
                      alt="Joe Johnson"
                      width={1200}
                      height={1607}
                      loading="lazy"
                      decoding="async"
                      className="absolute inset-0 w-full h-full object-cover grayscale opacity-80 mix-blend-luminosity group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground" style="background-color: %231a1f2e;"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 0 0-16 0"/></svg>';
                      }}
                    />
                    <div className="absolute inset-0 ring-1 ring-inset ring-black/10 dark:ring-white/10" />
                  </div>
                  <div className="p-6 sm:p-8 w-full sm:w-3/5 md:w-2/3 flex flex-col justify-center">
                    <h4 className="text-2xl font-display font-bold text-foreground mb-1">Joe Johnson</h4>
                    <p className="text-primary font-mono text-sm tracking-wider mb-4">Chief Ideas Officer</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      With over 20 years of experience in technology and a deep focus in AI enablement for enterprise and SMB, Joe brings strategic vision and operational excellence to Pax Magellanic. His background in technology implementation and digital marketing has been instrumental in establishing AI practices in many companies in the US and the world.
                    </p>
                    <span className={VIEW_PROFILE_CLASS}>
                      View Profile
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
